import React, { useState, useRef, useEffect } from "react";
import {
  Trash2,
  Clock,
  Pencil,
  Check,
  X,
  MessageSquare,
  MoreHorizontal,
} from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";

/**
 * SessionsSidebar
 * Always-visible left rail listing every conversation (grouped by session_id).
 * Sticky to the viewport so the list stays reachable even when the main
 * conversation column is very long.
 */
export default function SessionsSidebar({
  sessions,
  activeSessionId,
  onSelect,
  onRename,
  onDelete,
  onNew,
}) {
  const { t } = useLang();
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (renamingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renamingId]);

  useEffect(() => {
    if (!menuOpenFor) return;
    const close = () => setMenuOpenFor(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuOpenFor]);

  const startRename = (s) => {
    setMenuOpenFor(null);
    setRenamingId(s.session_id);
    setRenameValue(s.title || "");
  };

  const commitRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingId(null);
      return;
    }
    await onRename(renamingId, trimmed);
    setRenamingId(null);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  return (
    <aside
      className="hidden md:flex sticky top-0 self-start w-72 flex-shrink-0 h-screen border-r border-white/10 bg-black/40 flex-col z-20"
      data-testid="sessions-sidebar"
    >
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="label-eyebrow">{t("sessions.eyebrow")}</div>
          <div className="font-display text-lg font-black tracking-tight">{t("sessions.title")}</div>
        </div>
        <button
          onClick={onNew}
          className="btn-ghost !h-8 !px-3 focus-ring"
          data-testid="new-search-btn"
        >
          <span className="text-[11px] uppercase tracking-[0.15em]">{t("history.new")}</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto thin-scroll min-h-0">
        {(!sessions || sessions.length === 0) && (
          <div className="p-6 label-eyebrow text-center opacity-60">
            {t("sessions.empty")}
          </div>
        )}
        <ul className="p-2">
          {sessions?.map((s) => {
            const active = s.session_id === activeSessionId;
            const isRenaming = renamingId === s.session_id;
            return (
              <li key={s.session_id}>
                <div
                  className={`group relative rounded-md px-3 py-2.5 mb-1 transition-colors ${
                    active
                      ? "bg-white/8 border border-white/15"
                      : "hover:bg-white/5 border border-transparent"
                  } ${isRenaming ? "" : "cursor-pointer"}`}
                  onClick={() => !isRenaming && onSelect(s)}
                  data-testid={`session-item-${s.session_id}`}
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
                    <Clock className="w-3 h-3" />
                    {new Date(s.last_active).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    <span className="ml-auto flex items-center gap-1 text-white/50">
                      <MessageSquare className="w-3 h-3" />
                      {s.search_count}
                    </span>
                  </div>

                  {isRenaming ? (
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        ref={inputRef}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename();
                          if (e.key === "Escape") cancelRename();
                        }}
                        className="cmd-input flex-1 h-8 px-2 rounded text-[13px]"
                        maxLength={120}
                        data-testid={`rename-input-${s.session_id}`}
                      />
                      <button
                        onClick={commitRename}
                        className="p-1.5 rounded hover:bg-white/10 focus-ring"
                        aria-label={t("sessions.saveRename")}
                        data-testid={`rename-confirm-${s.session_id}`}
                      >
                        <Check className="w-3.5 h-3.5 text-white/85" />
                      </button>
                      <button
                        onClick={cancelRename}
                        className="p-1.5 rounded hover:bg-white/10 focus-ring"
                        aria-label={t("sessions.cancelRename")}
                        data-testid={`rename-cancel-${s.session_id}`}
                      >
                        <X className="w-3.5 h-3.5 text-white/70" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="text-[13px] text-white/85 line-clamp-2 pr-6">
                        {s.title}
                      </div>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenFor(menuOpenFor === s.session_id ? null : s.session_id);
                          }}
                          className="absolute top-[-28px] right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/10 focus-ring"
                          aria-label={t("sessions.moreActions")}
                          data-testid={`session-menu-${s.session_id}`}
                        >
                          <MoreHorizontal className="w-3.5 h-3.5 text-white/70" />
                        </button>
                        {menuOpenFor === s.session_id && (
                          <div
                            className="absolute z-20 right-0 top-0 mt-1 w-40 rounded-md border border-white/10 bg-[#0f0f0f] shadow-xl overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`session-menu-panel-${s.session_id}`}
                          >
                            <button
                              onClick={() => startRename(s)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-white/85 hover:bg-white/5 focus-ring text-left"
                              data-testid={`session-rename-${s.session_id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                              {t("sessions.rename")}
                            </button>
                            <button
                              onClick={() => {
                                setMenuOpenFor(null);
                                if (window.confirm(t("sessions.deleteConfirm"))) {
                                  onDelete(s.session_id);
                                }
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[#ff9c85] hover:bg-[#ff9c85]/10 focus-ring text-left border-t border-white/5"
                              data-testid={`session-delete-${s.session_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {t("sessions.delete")}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}
